import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { useAuth } from './AuthContext'
import { rolesApi } from '@/lib/api'
import type { RolePermission } from '@/types'

// Screen codes that map to navigation items
export type ScreenCode =
  | 'DASHBOARD'
  | 'CLIENTS'
  | 'BUSINESSES'
  | 'PROJECTS'
  | 'QUOTES'
  | 'INVOICES'
  | 'RECURRING_INVOICES'
  | 'EXPENSES'
  | 'TIME_TRACKING'
  | 'TIME_REPORTS'
  | 'TAX_MASTER'
  | 'FEES_MASTER'
  | 'CATEGORY_MASTER'
  | 'PRODUCT_MASTER'
  | 'AGENCY_CONFIG'
  | 'CUSTOM_FIELDS'
  | 'BILLING_TEMPLATES'
  | 'EMAIL_TEMPLATES'
  | 'INVOICE_SETTINGS'
  | 'AUDIT_LOGS'
  | 'ROLES'
  | 'USERS'
  | 'SUPPORT'
  | 'CRM_LEADS'
  | 'CRM_SETTINGS'
  | 'CRM_PIPELINES'
  | 'CRM_DEALS'
  | 'CRM_AUTOMATIONS'
  | 'CRM_DASHBOARD'
  | 'CRM_REPORTS'
  | 'CONTRACTS'
  | 'CONTRACT_TEMPLATES'
  | 'PAYMENT_GATEWAYS'
  | 'PAYMENT_HISTORY'
  | 'PAYMENT_METHODS'
  | 'EXCHANGE_RATES'
  | 'SOCIAL_MEDIA_CALENDARS'
  | 'RECONCILIATION'

export interface PermissionContextType {
  permissions: RolePermission[]
  isLoading: boolean
  hasPermission: (screenCode: ScreenCode, action?: 'view' | 'add_edit' | 'approve') => boolean
  canView: (screenCode: ScreenCode) => boolean
  canAddEdit: (screenCode: ScreenCode) => boolean
  canApprove: (screenCode: ScreenCode) => boolean
  refreshPermissions: () => Promise<void>
}

const PermissionContext = createContext<PermissionContextType | undefined>(undefined)

export function PermissionProvider({ children }: { children: React.ReactNode }) {
  const { user, isAuthenticated, isSuperAdmin } = useAuth()
  const [permissions, setPermissions] = useState<RolePermission[]>([])
  const [isLoading, setIsLoading] = useState(false)

  const fetchPermissions = useCallback(async () => {
    if (!user?.role_id || !isAuthenticated) {
      setPermissions([])
      return
    }

    // Super admins have all permissions
    if (isSuperAdmin) {
      setPermissions([])
      return
    }

    setIsLoading(true)
    try {
      const role = await rolesApi.getById(user.role_id)
      setPermissions(role.permissions || [])
    } catch (error) {
      console.error('Failed to fetch permissions:', error)
      setPermissions([])
    } finally {
      setIsLoading(false)
    }
  }, [user?.role_id, isAuthenticated, isSuperAdmin])

  useEffect(() => {
    fetchPermissions()
  }, [fetchPermissions])

  const hasPermission = useCallback(
    (screenCode: ScreenCode, action: 'view' | 'add_edit' | 'approve' = 'view'): boolean => {
      // Super admins have all permissions
      if (isSuperAdmin) return true

      const permission = permissions.find((p) => p.screen_code === screenCode)
      if (!permission) return false

      switch (action) {
        case 'view':
          return permission.can_view
        case 'add_edit':
          return permission.can_add_edit
        case 'approve':
          return permission.can_approve
        default:
          return false
      }
    },
    [permissions, isSuperAdmin]
  )

  const canView = useCallback(
    (screenCode: ScreenCode): boolean => hasPermission(screenCode, 'view'),
    [hasPermission]
  )

  const canAddEdit = useCallback(
    (screenCode: ScreenCode): boolean => hasPermission(screenCode, 'add_edit'),
    [hasPermission]
  )

  const canApprove = useCallback(
    (screenCode: ScreenCode): boolean => hasPermission(screenCode, 'approve'),
    [hasPermission]
  )

  const value: PermissionContextType = {
    permissions,
    isLoading,
    hasPermission,
    canView,
    canAddEdit,
    canApprove,
    refreshPermissions: fetchPermissions,
  }

  return <PermissionContext.Provider value={value}>{children}</PermissionContext.Provider>
}

export function usePermissions() {
  const context = useContext(PermissionContext)
  if (context === undefined) {
    throw new Error('usePermissions must be used within a PermissionProvider')
  }
  return context
}

